import { useMemo } from 'react'

import { useSwapQuoteForm } from '~/components/forms/swap/hooks/useSwapQuoteForm'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { useTokenPrices } from '~/hooks/useTokenPrices'

/**
 * Calculates the price impact of the current swap quote by comparing
 * the USD value of the sell side vs the buy side.
 *
 * Returns both a percentage and absolute USD impact.
 */
export const useSwapPriceImpact = ({
  isLoadingQuote,
}: {
  isLoadingQuote?: boolean
}) => {
  const swapQuote = useSwapQuoteForm()
  const { convertTokenToUSD } = useTokenPrices()

  const sellTokenMetadata = useTokenMetadata({
    address: swapQuote?.sellToken,
    chainId: swapQuote?.chainId,
    includePrice: true,
  })
  const buyTokenMetadata = useTokenMetadata({
    address: swapQuote?.buyToken,
    chainId: swapQuote?.destinationChainId,
    includePrice: true,
  })

  return useMemo(() => {
    if (
      !swapQuote ||
      !sellTokenMetadata?.price ||
      !buyTokenMetadata?.price ||
      isLoadingQuote
    ) {
      return { priceImpactUSD: null, priceImpactPercentage: null }
    }

    try {
      const sellAmountUSD = convertTokenToUSD({
        token: swapQuote.sellToken,
        amount: swapQuote.sellAmount,
        chainId: swapQuote.chainId,
      })

      const buyAmountUSD = convertTokenToUSD({
        token: swapQuote.buyToken,
        amount: swapQuote.buyAmount,
        chainId: swapQuote.destinationChainId,
      })

      if (sellAmountUSD === undefined || buyAmountUSD === undefined) {
        return { priceImpactUSD: null, priceImpactPercentage: null }
      }

      const usdImpact = buyAmountUSD - sellAmountUSD
      const percentage = (usdImpact / sellAmountUSD) * 100

      return {
        priceImpactUSD: usdImpact,
        priceImpactPercentage: isFinite(percentage) ? percentage : null,
      }
    } catch {
      return { priceImpactUSD: null, priceImpactPercentage: null }
    }
  }, [
    swapQuote,
    convertTokenToUSD,
    sellTokenMetadata?.price,
    buyTokenMetadata?.price,
    isLoadingQuote,
  ])
}
